'use client'

import { createContext, ReactNode, useContext, useEffect, useReducer } from 'react'
import authService from '../service/auth.service'
import { AuthState, JWTActions, JWTContextType, Types } from '../types/auth.types'
import { isValidToken, setSession } from '../utils/jwt.utils'

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
}

const JWTReducer = (state: AuthState, action: JWTActions) => {
  switch (action.type) {
    case 'INITIALIZE':
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      }
    case 'LOGIN':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
      }
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
      }
    default:
      return state
  }
}

const AuthContext = createContext<JWTContextType | null>(null)

// ----------------------------------------------------------------------

type AuthProviderProps = {
  children: ReactNode
}

function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(JWTReducer, initialState)

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = window.localStorage.getItem('accessToken')
        const remember = window.localStorage.getItem('remember')

        // only load user if token and remember flag is present
        // tolen is always saved, remember is saved if selected during login
        if (accessToken && isValidToken(accessToken) && remember === 'true') {
          setSession(accessToken)

          const user = await authService.getCurrentUser(accessToken)

          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: true, user },
          })
        } else {
          dispatch({
            type: Types.Initial,
            payload: { isAuthenticated: false, user: null },
          })
        }
      } catch (err) {
        console.error(err)
        dispatch({
          type: Types.Initial,
          payload: { isAuthenticated: false, user: null },
        })
      }
    }

    initialize()
  }, [])

  const login = async (email: string, password: string, remember: boolean) => {
    const { token, user } = await authService.login({ email, password })

    setSession(token)

    // set remember value, only load user if token and remember flag is present
    localStorage.setItem('remember', remember ? 'true' : 'false')

    dispatch({ type: Types.Login, payload: { user } })
  }

  const logout = async () => {
    setSession(null)
    dispatch({ type: Types.Logout })
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const useAuth = () => {
  const context = useContext(AuthContext)

  if (!context) throw new Error('Auth context must be use inside AuthProvider')

  return context
}

export { AuthContext, AuthProvider, useAuth }
